export const MAX_SUM = 700

export const indexToPackage = (answer, index) =>
  (answer.featureLengths || [])
    .reduce(
      ([arr, index], cur) => {
        arr.push(index % cur)
        return [arr, Math.floor(index / cur)]
      },
      [[], index]
    )[0]
    .map(v => v + 1)

export const packageToIndex = (answer, currentPackage) =>
  Array.from(currentPackage)
    .reverse()
    .reduce(
      (acc, cur, i) =>
        acc + (cur - 1) * (answer.featureLengths || []).slice(0, -i - 1).reduce((_acc, _cur) => _acc * _cur, 1),
      0
    )

// A summarised slice keeps `clusters` but strips the arrays inside each one, exactly as it strips
// their top-level twins -- measured on the stand, where a cluster falls from thirteen keys to four.
// So every per-cluster read resolves through the same shape as its top-level twin and answers
// undefined rather than throwing; the block draws nothing, which is what the summarised verdict
// already asks for one layer up.
const seriesOf = (answer, persona, key) => (persona ? answer?.clusters?.[persona - 1]?.[key] : answer?.[key])

const packageShares = (answer, persona) => seriesOf(answer, persona, 'packageShares')

const packageScores = (answer, persona) => seriesOf(answer, persona, 'packageScores')

// const packageRank = (mode, answer, persona) =>
//   'sum' == mode ? packageRankScores(answer, persona) : packageRankShares(answer, persona)

// const packageRankShares = (answer, persona) =>
//   persona ? answer.clusters[persona - 1].packageRankShares : answer.packageRankShares

// const packageRankScores = (answer, persona) =>
//   persona ? answer.clusters[persona - 1].packageRankScores : answer.packageRankScores

const tickMarksShares = (answer, persona) => seriesOf(answer, persona, 'tickMarksShares')

const tickMarksScores = (answer, persona) => seriesOf(answer, persona, 'tickMarksScores')

const tickMarks = (mode, answer, persona) =>
  'sum' == mode ? tickMarksScores(answer, persona) : tickMarksShares(answer, persona)

export const calcImportance = (answer, bPosition, persona) => {
  const config = answer?.config || { v: 1 }

  const key = answer.importance ? 'importance' : 1 == config.v ? 'importance_aggregate' : 'importance_individual'
  // zero rather than undefined: one caller sorts on this, and a NaN there makes the order arbitrary
  return seriesOf(answer, persona, key)?.[bPosition - 1] ?? 0
}

export const calcAdjustedCoefficients = (answer, persona, start, end) =>
  seriesOf(answer, persona, 'adjusted_coefficients')?.slice(start, end) ?? []

// Unlike the other per-cluster reads, a persona without its own NA coefficient falls back to the
// aggregate one, so its No-answer share does not collapse to zero while the aggregate carries a value.
export const calcNACoefficient = (answer, persona) =>
  Math.min(MAX_SUM, (seriesOf(answer, persona, 'NACoeffRaw') ?? answer?.NACoeffRaw)?.[0] ?? -999)

export const calcBCoefficients = (answer, bPosition, persona) => {
  const start = (answer.featureLengths || []).slice(0, bPosition - 1).reduce((acc, cur) => acc + cur, 0)
  const end = start + (answer.featureLengths || [])[bPosition - 1]
  return calcAdjustedCoefficients(answer, persona, start, end)
}

export const calcPositionSum = (mode, answer, persona, currentPackage) => {
  const config = answer?.config || { v: 1 }

  if (1 == config.v) {
    return (answer.featureLengths || []).reduce((acc, _, i) => {
      const cs = calcBCoefficients(answer, i + 1, persona)
      return acc + cs[currentPackage[i] - 1]
    }, 0)
  } else {
    const index = packageToIndex(answer, currentPackage)
    const res = 'sum' == mode ? packageScores(answer, persona) : packageShares(answer, persona)
    return res ? (res[index] ?? 0) : 0
  }
}

export const calcPosition = (mode, answer, persona, currentPackage) => {
  const config = answer?.config || { v: 1 }

  if (1 == config.v) {
    const sum = calcPositionSum(mode, answer, persona, currentPackage)
    const exp = Math.exp(Math.min(MAX_SUM, sum))
    return 'sum' == mode ? sum : exp / (1 + exp)
  } else {
    return calcPositionSum(mode, answer, persona, currentPackage)
  }
}

export const calcPercent = (mode, value) => ('sum' == mode ? 1 : 100) * value

export const calcPackage = (mode, answer, coef, persona) => {
  const config = answer?.config || { v: 1 }

  let currentPackage = []

  if (1 == config.v) {
    let k = 0
    currentPackage = (answer.featureLengths || []).map((_, i) => {
      const cs = calcBCoefficients(answer, i + 1, persona)
      const values = cs.map((v, j) => [v, j + 1])
      values.sort((a, b) => a[0] - b[0])
      const median =
        0.5 == coef
          ? values.length % 2
            ? Math.ceil(values.length / 2)
            : values.length / 2 + (k % 2 ? 0 : 1)
          : Math.ceil(coef * values.length)
      if (0.5 == coef && !(values.length % 2)) k++
      return values[median - 1][1]
    })
  } else {
    const packages = tickMarks(mode, answer, persona)
    if (!packages) return currentPackage

    switch (coef) {
      case 0:
        currentPackage = packages[4]
        break
      case 0.25:
        currentPackage = packages[3]
        break
      case 0.5:
        currentPackage = packages[2]
        break
      case 0.75:
        currentPackage = packages[1]
        break
      case 1:
        currentPackage = packages[0]
        break
    }
  }

  return currentPackage
}

export const calcBestPackage = (mode, answer, persona = 0, packageRankSharesArr, packageRankScoresArr) => {
  if (0 < packageRankScoresArr?.length && 0 < packageRankSharesArr?.length) {
    const arr = 'sum' == mode ? packageRankScoresArr[persona] : packageRankSharesArr[persona]
    return indexToPackage(answer, arr[0])
  } else {
    return (answer.featureLengths || []).map((_, i) => {
      const cs = calcBCoefficients(answer, i + 1, persona)
      const max = Math.max.apply(null, cs)
      return cs.indexOf(max) + 1
    })
  }
}

export const calcMedianPackage = (mode, answer, persona) => calcPackage(mode, answer, 0.5, persona)

export const calcWorstPackage = (mode, answer, persona = 0, packageRankSharesArr, packageRankScoresArr) => {
  if (0 < packageRankScoresArr?.length && 0 < packageRankSharesArr?.length) {
    const arr = 'sum' == mode ? packageRankScoresArr[persona] : packageRankSharesArr[persona]
    return indexToPackage(answer, arr[arr.length - 1])
  } else {
    return (answer.featureLengths || []).map((_, i) => {
      const cs = calcBCoefficients(answer, i + 1, persona)
      const min = Math.min.apply(null, cs)
      return cs.indexOf(min) + 1
    })
  }
}

// Swap one attribute's selected answer-variant in the current package string.
export const positionsToPackage = (currentPackage, bPosition, avPosition) => {
  const pkg = String(currentPackage)
    .split('.')
    .map(v => parseInt(v, 10))
  pkg[bPosition - 1] = avPosition
  return pkg
}

// Per-batch answer for one subquestion row: `a<pos>` coefficient/impact values, the
// selected variant, and the importance %. Mirrors the legacy BatchesView#calcBatchAnswer;
// `currentPackageCoeffs` is calcPercent(mode, calcPosition(...)) for the current package.
export const calcBatchAnswer = ({
  answer,
  batch,
  mode,
  persona,
  currentPackage,
  currentPackageCoeffs,
  isSummaryView
}) => {
  const pkg = String(currentPackage)
    .split('.')
    .map(v => parseInt(v, 10))
  const factor = 'sum' == mode ? 100 : 10

  const bAnswer = batch.answerVariants.reduce((acc, cur) => {
    let value
    if ('sum' == mode) {
      value = calcBCoefficients(answer, batch.position, persona)[cur.position - 1]
    } else {
      const newPackage = Array.from(pkg)
      newPackage.splice(batch.position - 1, 1, cur.position)
      value = calcPercent(mode, calcPosition(mode, answer, persona, newPackage)) - currentPackageCoeffs
    }
    acc[`a${cur.position}`] = {
      value: Math.round(factor * value) / factor,
      original: value,
      prefix: 'sum' == mode ? '' : '%'
    }
    return acc
  }, {})

  if (0 < batch.answerVariants.length && !isSummaryView) {
    bAnswer.selected = pkg[batch.position - 1]
  }
  bAnswer.importance = 100 * calcImportance(answer, batch.position, persona)
  bAnswer.importance_title = 'sum' == mode ? 'Utility score' : 'Probability impact'

  return bAnswer
}

// calcPercent(mode, calcPosition(...)) for the currently-selected package — the baseline
// each variant's probability impact is measured against.
export const calcCurrentPackageCoeffs = (mode, answer, persona, currentPackage) => {
  const pkg = String(currentPackage)
    .split('.')
    .map(v => parseInt(v, 10))
  return calcPercent(mode, calcPosition(mode, answer, persona, pkg))
}
